/**
 * SectionPropertiesPanel — Sprint M
 *
 * Right-side inspector for the currently-selected section. Handles name,
 * capacity, pricing zone assignment, rotation, decorative toggle, label
 * display, icon and delete. Changes patch onto the parent's local state
 * (auto-save is debounced in the parent).
 */
import React, { useEffect, useState } from "react";
import { Info, Trash2, Copy } from "lucide-react";

const LABEL_DISPLAY_OPTIONS = [
  { value: "always", label: "Always visible" },
  { value: "on_hover", label: "On hover only" },
  { value: "hidden", label: "Hidden" },
];

const DECOR_ICON_OPTIONS = [
  { value: null, label: "None" },
  { value: "stage", label: "Stage" },
  { value: "entrance", label: "Entrance" },
  { value: "broadcast", label: "Broadcast" },
  { value: "sound", label: "Sound desk" },
  { value: "camera", label: "Camera" },
];

export default function SectionPropertiesPanel({
  section,
  zones,
  disabled,
  onPatch,
  onDelete,
  onDuplicate,
}) {
  const [local, setLocal] = useState(section || {});

  useEffect(() => {
    setLocal(section || {});
  }, [section]);

  if (!section) {
    return (
      <div
        className="border border-[var(--gt-line)] rounded-sm bg-white p-4 text-xs text-[var(--gt-ink-3)]"
        data-testid="section-props-empty"
      >
        <div className="gt-label mb-2 flex items-center gap-1.5">
          <Info size={11} /> Section properties
        </div>
        <p>Select a section on the canvas to edit its name, colour zone, rotation, capacity and more.</p>
      </div>
    );
  }

  const commit = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onPatch?.(patch);
  };

  return (
    <div
      className="border border-[var(--gt-line)] rounded-sm bg-white p-3 space-y-3"
      data-testid={`section-props-${section.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="gt-label flex items-center gap-1.5">
          <Info size={11} /> Section
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onDuplicate}
            disabled={disabled}
            className="gt-btn-ghost text-[10px]"
            data-testid="section-duplicate-btn"
            aria-label="Duplicate section"
          >
            <Copy size={11} /> Duplicate
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={disabled}
            className="gt-btn-ghost text-[10px] text-[var(--gt-accent)]"
            data-testid="section-delete-btn"
            aria-label="Delete section"
          >
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Name</label>
        <input
          type="text"
          value={local.name || ""}
          onChange={(e) => commit({ name: e.target.value })}
          disabled={disabled}
          className="gt-input w-full text-xs"
          data-testid="section-name-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Capacity</label>
          <input
            type="number"
            min="0"
            value={local.capacity ?? 0}
            onChange={(e) => commit({ capacity: parseInt(e.target.value || "0", 10) })}
            disabled={disabled || local.is_decorative}
            className="gt-input w-full text-xs"
            data-testid="section-capacity-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Rotation°</label>
          <input
            type="number"
            min="-180"
            max="180"
            step="1"
            value={local.rotation_deg ?? 0}
            onChange={(e) => commit({ rotation_deg: parseFloat(e.target.value || "0") })}
            disabled={disabled}
            className="gt-input w-full text-xs"
            data-testid="section-rotation-input"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Pricing zone</label>
        <select
          value={local.pricing_zone_id || ""}
          onChange={(e) => commit({ pricing_zone_id: e.target.value || null })}
          disabled={disabled || local.is_decorative}
          className="gt-input w-full text-xs"
          data-testid="section-zone-select"
        >
          <option value="">— None —</option>
          {(zones || []).map((z) => (
            <option key={z.id} value={z.id}>{z.name} · A${((z.base_price_cents || 0) / 100).toFixed(2)}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Label display</label>
        <select
          value={local.label_display || "always"}
          onChange={(e) => commit({ label_display: e.target.value })}
          disabled={disabled}
          className="gt-input w-full text-xs"
          data-testid="section-label-display-select"
        >
          {LABEL_DISPLAY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <label className="flex items-center gap-2 text-xs" data-testid="section-decorative-label">
        <input
          type="checkbox"
          checked={!!local.is_decorative}
          onChange={(e) => commit({ is_decorative: e.target.checked })}
          disabled={disabled}
          data-testid="section-decorative-checkbox"
        />
        <span>Decorative block (STAGE / entrance / camera / etc.) — no seats, no capacity</span>
      </label>

      {local.is_decorative && (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Icon</label>
          <select
            value={local.icon_name || ""}
            onChange={(e) => commit({ icon_name: e.target.value || null })}
            disabled={disabled}
            className="gt-input w-full text-xs"
            data-testid="section-icon-select"
          >
            {DECOR_ICON_OPTIONS.map((o) => (
              <option key={o.value || "none"} value={o.value || ""}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="pt-2 border-t border-[var(--gt-line)] grid grid-cols-2 gap-2 text-[10px] text-[var(--gt-ink-3)] font-mono">
        <div>x: {(local.x_percent ?? 0).toFixed(1)}%</div>
        <div>y: {(local.y_percent ?? 0).toFixed(1)}%</div>
        <div>w: {(local.width_percent ?? 0).toFixed(1)}%</div>
        <div>h: {(local.height_percent ?? 0).toFixed(1)}%</div>
      </div>
    </div>
  );
}
